import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { AdminGridColumnsFactory } from "../admin-grid/admin-grid-columns-factory";
import { SelectionChangedEvent } from "devextreme/ui/data_grid";
import { CustomItemCreatingEvent, SelectionChangedEvent as SelectBoxChanged } from "devextreme/ui/select_box";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { combineLatest, map } from "rxjs";
import { ToolbarItem, ToolbarItemFactory } from "../toolbar";
import { MetadataService } from "src/app/services/metadata.service";
import { DxDataGridComponent } from "devextreme-angular";
import { brdCatchError } from "../message-utils";

@Component({
  selector: "brd-usecases-grid",
  templateUrl: "./usecases-grid.component.html",
  styleUrl: "./usecases-grid.component.less",
  standalone: false
})
export class UsecasesGridComponent implements OnInit {
  @Input() selectedRowKey: number[];
  @Input() enableDelete: boolean = false;
  @Input() customItems: ToolbarItem[] = [];
  @Output() selectionChanged = new EventEmitter<number[]>();
  @ViewChild(DxDataGridComponent) useCaseGrid: DxDataGridComponent;

  visible = false;
  visibleDelete = false;
  columnsUseCases = [];
  filteredProcesses = [];
  selectedUseCases = [];
  domainEditorOptions: unknown;
  useCaseFormGroup = new FormGroup({
    id: new FormControl(0),
    name: new FormControl("", Validators.required),
    domain: new FormControl(null, Validators.required),
    process: new FormControl(null, Validators.required)
  });
  saveBtnOptions = {
    text: "Save",
    onClick: (): void => this.confirmChanges()
  };
  cancelBtnOptions = {
    text: "Cancel",
    onClick: (): void => {
      this.hidePopup();
    }
  };

  deleteBtnOptions = {
    text: "Yes",
    onClick: (): void => {
      this.deleteUseCases();
    }
  };

  cancelDeleteBtnOptions = {
    text: "No",
    onClick: (): void => this.hideDeletePopup()
  };

  useCases$ = combineLatest([this.metadataService.useCases$, this.metadataService.filterUseCases$]).pipe(
    map(([useCases, filterIds]) => {
      if (!filterIds) return useCases;
      return useCases.filter(x => filterIds.includes(x.id));
    })
  );

  processes = [];
  domains = [];

  toolbarItems: ToolbarItem[];

  constructor(private metadataService: MetadataService) {}

  ngOnInit(): void {
    combineLatest({
      processes: this.metadataService.processes$,
      domains: this.metadataService.domains$
    })
      .pipe(
        map(({ processes, domains }) => {
          this.processes = [...processes];
          this.filteredProcesses = [...processes];
          this.domains = [...domains];

          this.columnsUseCases = [
            AdminGridColumnsFactory.createTextColumn({ fieldName: "id", caption: "id" }, { visible: false, allowEditing: false }),
            AdminGridColumnsFactory.createTextColumn({ fieldName: "name", caption: "Name" }),
            AdminGridColumnsFactory.createLookupColumn({
              fieldName: "processId",
              caption: "Process",
              lookup: { dataSource: processes, displayExpr: "name", valueExpr: "id" }
            }),
            AdminGridColumnsFactory.createLookupColumn({
              fieldName: "domainId",
              caption: "Domain",
              lookup: { dataSource: domains, displayExpr: "name", valueExpr: "id" }
            }),
            AdminGridColumnsFactory.createButtonsColumn({
              buttons: [
                {
                  icon: "edit",
                  onClick: (e): void => {
                    this.showPopup({ ...e.row.data });
                  }
                }
              ]
            })
          ];
        })
      )
      .subscribe();

    this.toolbarItems = [
      ToolbarItemFactory.createButton({
        icon: "add",
        text: "Use case",
        onClick: () => {
          this.addRow();
        }
      }),
      ...this.customItems
    ];
  }

  onSelectionChanged(e: SelectionChangedEvent): void {
    if (this.enableDelete) {
      if (e.selectedRowKeys.length > 0 && this.toolbarItems.length === 1) {
        this.toolbarItems.push(
          ToolbarItemFactory.createButton({
            icon: "trash",
            text: "Delete",
            type: "danger",
            onClick: () => {
              this.selectedUseCases = this.useCaseGrid.instance.getSelectedRowsData().map(x => x.name);
              this.visibleDelete = true;
            }
          })
        );
      } else if (e.selectedRowKeys.length === 0 && this.toolbarItems.length > 1) {
        this.toolbarItems = this.toolbarItems.slice(0, 1);
      }
    }

    this.selectionChanged?.emit(e.selectedRowKeys);
  }

  addRow(): void {
    this.showPopup({});
  }

  showPopup(formData): void {
    this.filteredProcesses = formData?.domainId
      ? this.processes.filter(p => p.domainId === formData.domainId || p.id === 0)
      : this.processes;

    this.useCaseFormGroup.setValue({
      id: formData?.id ?? 0,
      name: formData?.name ?? "",
      domain: formData?.domainId ? this.domains.find(d => d.id === formData.domainId) : null,
      process: formData?.processId ? this.processes.find(p => p.id === formData.processId) : null
    });
    this.visible = true;
  }

  hidePopup = (): void => {
    this.useCaseFormGroup.reset();
    this.visible = false;
  };

  hideDeletePopup = (): void => {
    this.visibleDelete = false;
  };

  onDomainChanged(e: SelectBoxChanged): void {
    this.filteredProcesses = e.selectedItem ? this.processes.filter(p => p.domainId === e.selectedItem.id || p.id === 0) : this.processes;
  }

  onProcessChanged(e: SelectBoxChanged): void {
    if (e.selectedItem?.domainId) {
      this.useCaseFormGroup.controls.domain.setValue(this.domains.find(d => d.id === e.selectedItem.domainId));
    }
  }

  onCustomItemCreating(e: CustomItemCreatingEvent): void {
    if (e.text) {
      const newItem = { id: 0, name: e.text };
      e.customItem = newItem;
    }
  }

  confirmChanges(): void {
    if (this.useCaseFormGroup.invalid) {
      this.useCaseFormGroup.markAllAsTouched();
      return;
    }

    const { id, name, domain, process } = this.useCaseFormGroup.value;
    this.metadataService
      .addUseCase({
        id,
        name,
        domainId: domain.id,
        domainName: domain.name,
        processId: process.id,
        processName: process.name
      })
      .pipe(
        map(res => {
          this.hidePopup();
          return res;
        }),
        brdCatchError()
      )
      .subscribe();
  }

  deleteUseCases(): void {
    const useCaseIds = this.useCaseGrid.instance.getSelectedRowKeys();

    this.metadataService
      .deleteUseCases(useCaseIds)
      .pipe(
        map(res => {
          this.visibleDelete = false;
          return res;
        })
      )
      .subscribe();
  }
}
